import type { IngridAddress, IngridSession, OrderAddressDTO } from 'ecosystem'
import { convertMinorUnitToValue } from 'shared-utils'
import type { BuiltShipment } from './buildShipmentFromPostnord'

/**
 * Map an Ingrid checkout session to the canonical `ShipmentDTO`.
 *
 * `shipmentId` is set to the Ingrid `checkout_session_id` so the FE/BE can
 * resolve the session later (e.g. carrier widget rehydration after refresh).
 * On the first DRAFT create the session has no carrier picked yet, so most
 * fields fall through; once the shopper completes the widget, `delivery_groups[0]`
 * is populated and this returns a fully fleshed-out shipment.
 */
export const buildShipmentFromIngrid = (
  ingridResponse?: IngridSession
): BuiltShipment | undefined => {
  if (!ingridResponse) return undefined

  /* eslint-disable camelcase -- ingrid wire shape */
  const { delivery_groups, checkout_session_id } = ingridResponse
  const deliveryGroup = delivery_groups?.[0]

  if (!deliveryGroup) {
    return {
      shipment: {
        shippingProvider: 'INGRID',
        ...(checkout_session_id ? { shipmentId: checkout_session_id } : {})
      }
    }
  }

  const deliveryAddress = deliveryGroup.addresses.delivery_address
  const shipping = deliveryGroup.shipping

  const toOrderAddress = (address?: IngridAddress): OrderAddressDTO | undefined =>
    address
      ? {
          address1: address.address_lines?.join(', ') || '',
          address2: '',
          country: address.country || '',
          mobilePhone: deliveryAddress?.phone || '',
          postalCode: address.postal_code || '',
          postalRegion: address.city || '',
          attention:
            `${deliveryAddress?.first_name ?? ''} ${deliveryAddress?.last_name ?? ''}`.trim(),
          reference: `${shipping.carrier} ${shipping.delivery_type}, ${shipping.product}`
        }
      : undefined

  const recipientAddress = toOrderAddress(deliveryAddress)
  const pickupAddress = toOrderAddress(deliveryGroup.addresses.location?.address)
  const address = pickupAddress ?? recipientAddress

  const shippingType: 'PICKUP' | 'DELIVERY' =
    shipping.delivery_type === 'instore' ? 'PICKUP' : 'DELIVERY'
  const carrierName = shipping.carrier
  const carrierService = shipping.product
  const trackingNumber = deliveryGroup.tos_id
  const locationId = deliveryGroup.addresses.location?.external_id
  const locationName = deliveryGroup.addresses.location?.name
  const shippingCost = convertMinorUnitToValue(deliveryGroup.pricing.price)

  return {
    shippingCost,
    shipment: {
      shippingProvider: 'INGRID',
      shippingType,
      ...(checkout_session_id ? { shipmentId: checkout_session_id } : {}),
      ...(carrierName ? { carrierName } : {}),
      ...(carrierService ? { carrierService } : {}),
      ...(trackingNumber ? { trackingNumber } : {}),
      ...(address ? { address } : {}),
      ...(locationId ? { locationId } : {}),
      ...(locationName ? { locationName } : {})
    }
  }
}
